/**
 * BadgeChips — renders a structured NECS badge as color-coded pill chips.
 * Helps admins scan dozens of pending requests and spot duplicates instantly.
 */
import {
  parseBadge,
  DEPT_LABEL_BY_CODE,
  ADMIN_POSITION_LABEL_BY_CODE,
  STAFF_ROLE_LABEL_BY_CODE,
  STAFF_POSITION_LABEL_BY_CODE,
} from '../utils/badgeParser';

const CHIP_PALETTE = {
  kind:     { bg: 'rgba(201,169,98,0.18)', fg: '#C9A962', border: 'rgba(201,169,98,0.45)' },     // gold
  dept:     { bg: 'rgba(99,179,237,0.18)',  fg: '#63B3ED', border: 'rgba(99,179,237,0.45)' },     // blue
  role:     { bg: 'rgba(56,189,248,0.18)',  fg: '#38BDF8', border: 'rgba(56,189,248,0.45)' },     // cyan
  position: { bg: 'rgba(192,132,252,0.18)', fg: '#C084FC', border: 'rgba(192,132,252,0.45)' },    // purple
  region:   { bg: 'rgba(251,146,60,0.18)',  fg: '#FB923C', border: 'rgba(251,146,60,0.45)' },     // orange
  state:    { bg: 'rgba(74,222,128,0.18)',  fg: '#4ADE80', border: 'rgba(74,222,128,0.45)' },     // green
  sequence: { bg: 'rgba(161,161,170,0.18)', fg: '#A1A1AA', border: 'rgba(161,161,170,0.45)' },    // gray
};

function Chip({ kind, label, title, testid }) {
  const p = CHIP_PALETTE[kind] || CHIP_PALETTE.sequence;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: p.bg, color: p.fg, border: `1px solid ${p.border}` }}
      title={title}
      data-testid={testid}
    >
      {label}
    </span>
  );
}

export default function BadgeChips({ badge }) {
  const parsed = parseBadge(badge);
  if (!parsed.ok) {
    return (
      <span className="font-mono text-xs text-zinc-400" data-testid="badge-chips-legacy">
        {parsed.raw || '—'}
      </span>
    );
  }

  if (parsed.kind === 'admin') {
    return (
      <div className="flex flex-wrap items-center gap-1" data-testid="badge-chips-admin">
        <Chip kind="kind"     label="ADMIN"           title="NECS Admin badge"                                  testid="badge-chip-kind" />
        <Chip kind="dept"     label={parsed.dept}     title={`Department · ${DEPT_LABEL_BY_CODE[parsed.dept] || parsed.dept}`} testid="badge-chip-dept" />
        <Chip kind="position" label={parsed.position} title={`Position · ${ADMIN_POSITION_LABEL_BY_CODE[parsed.position] || parsed.position}`} testid="badge-chip-position" />
        <Chip kind="state"    label={parsed.state}    title={`State · ${parsed.state}`}                          testid="badge-chip-state" />
        <Chip kind="sequence" label={`#${parsed.sequence}`} title="Sequence within Dept · Position · State"     testid="badge-chip-sequence" />
      </div>
    );
  }

  // staff
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="badge-chips-staff">
      <Chip kind="kind"     label="STAFF"           title="NECS Field Staff badge"                                       testid="badge-chip-kind" />
      <Chip kind="role"     label={parsed.role}     title={`Role · ${STAFF_ROLE_LABEL_BY_CODE[parsed.role] || parsed.role}`}     testid="badge-chip-role" />
      <Chip kind="position" label={parsed.position} title={`Position · ${STAFF_POSITION_LABEL_BY_CODE[parsed.position] || parsed.position}`} testid="badge-chip-position" />
      <Chip kind="region"   label={parsed.region}   title={`Region · ${parsed.region}`}                                  testid="badge-chip-region" />
      <Chip kind="state"    label={parsed.state}    title={`State · ${parsed.state}`}                                    testid="badge-chip-state" />
      <Chip kind="sequence" label={`#${parsed.sequence}`} title="Sequence within Role · Position · Region · State"       testid="badge-chip-sequence" />
    </div>
  );
}
